import React, { useState, useEffect } from 'react';
import { useWriteContract, useWaitForTransactionReceipt } from 'wagmi';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { toast } from 'sonner';
import { 
    CheckCircle, 
    ExternalLink,
    Loader2,
    Sparkles,
    Clock,
    ArrowRight
} from 'lucide-react';
import { getExplorerUrl } from '../lib/contract';

// UMA OptimisticOracleV3 ABI for settlement
const UMA_ORACLE_ABI = [
    {
        "type": "function",
        "name": "settleAssertion",
        "inputs": [{ "name": "assertionId", "type": "bytes32" }],
        "outputs": [],
        "stateMutability": "nonpayable"
    },
    {
        "type": "function",
        "name": "getAssertion",
        "inputs": [{ "name": "assertionId", "type": "bytes32" }],
        "outputs": [{
            "name": "",
            "type": "tuple",
            "components": [
                { "name": "escalationManagerSettings", "type": "tuple", "components": [] },
                { "name": "asserter", "type": "address" },
                { "name": "assertionTime", "type": "uint64" },
                { "name": "settled", "type": "bool" },
                { "name": "currency", "type": "address" },
                { "name": "expirationTime", "type": "uint64" },
                { "name": "settlementResolution", "type": "bool" },
                { "name": "domainId", "type": "bytes32" },
                { "name": "identifier", "type": "bytes32" },
                { "name": "bond", "type": "uint256" },
                { "name": "callbackRecipient", "type": "address" },
                { "name": "disputer", "type": "address" }
            ]
        }],
        "stateMutability": "view"
    }
];

export function OracleSettlementPanel({ 
    bet, 
    chainId, 
    address,
    oracleAddress 
}) {
    const [timeRemaining, setTimeRemaining] = useState(null);
    const [canSettle, setCanSettle] = useState(false);
    
    const assertionId = bet?.assertion_id;
    const creatorWinsAsserted = bet?.creator_wins_asserted;
    const assertionTime = bet?.assertion_requested_at;
    
    // Calculate time remaining for challenge
    useEffect(() => {
        if (!assertionTime) return;
        
        const updateTime = () => {
            const assertedAt = new Date(assertionTime);
            const expiresAt = new Date(assertedAt.getTime() + 2 * 60 * 60 * 1000); // 2 hours
            const now = new Date();
            const remaining = expiresAt - now;
            
            if (remaining <= 0) {
                setTimeRemaining(null);
                setCanSettle(true);
            } else {
                const hours = Math.floor(remaining / 3600000);
                const minutes = Math.floor((remaining % 3600000) / 60000);
                const seconds = Math.floor((remaining % 60000) / 1000);
                setTimeRemaining({ hours, minutes, seconds });
                setCanSettle(false);
            }
        };
        
        updateTime();
        const interval = setInterval(updateTime, 1000);
        return () => clearInterval(interval);
    }, [assertionTime]);
    
    // Settlement contract call
    const { 
        writeContract: settleAssertion, 
        data: settlementHash,
        isPending: isSettlePending,
        error: settleError
    } = useWriteContract();
    
    const { isLoading: isSettleConfirming, isSuccess: isSettleSuccess } = 
        useWaitForTransactionReceipt({ hash: settlementHash });
    
    const handleSettle = async () => {
        if (!assertionId || !oracleAddress) return;
        
        try {
            // Ensure assertionId is properly formatted as bytes32
            const formattedId = assertionId.startsWith('0x') ? assertionId : `0x${assertionId}`;
            
            await settleAssertion({
                address: oracleAddress,
                abi: UMA_ORACLE_ABI,
                functionName: 'settleAssertion',
                args: [formattedId]
            });
        } catch (error) {
            console.error('Settlement error:', error);
            toast.error('Failed to settle assertion');
        }
    };
    
    // Effect for settlement success
    useEffect(() => {
        if (isSettleSuccess && settlementHash) {
            toast.success('Assertion settled! Bet resolved.', {
                description: (
                    <a 
                        href={getExplorerUrl(chainId, 'tx', settlementHash)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#00FF66] hover:underline flex items-center gap-1"
                    >
                        View transaction <ExternalLink className="w-3 h-3" />
                    </a>
                ),
            });
        }
    }, [isSettleSuccess, settlementHash, chainId]);
    
    // Only show if bet is in assertion and time has expired
    if (bet?.status !== 'in_assertion' || !assertionId || !canSettle) {
        return null;
    }
    
    return (
        <Card className="bg-[#00FF66]/5 border-[#00FF66]/20" data-testid="oracle-settlement-panel">
            <CardHeader className="pb-3">
                <CardTitle className="text-lg text-white flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-[#00FF66]" />
                    Ready for Settlement
                </CardTitle>
                <CardDescription className="text-white/50">
                    Challenge period has ended - finalize the bet resolution
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                {/* Success State */}
                <div className="p-4 rounded-lg bg-[#00FF66]/10 border border-[#00FF66]/20">
                    <div className="flex items-center gap-2 mb-2">
                        <CheckCircle className="w-5 h-5 text-[#00FF66]" />
                        <span className="text-[#00FF66] font-semibold">
                            No Disputes - Assertion Accepted
                        </span>
                    </div>
                    <p className="text-sm text-white/60">
                        The 2-hour challenge window has passed without any disputes.
                        The asserted outcome is now final and can be settled.
                    </p>
                </div>
                
                {/* Outcome */}
                <div className="flex items-center justify-between p-3 rounded-lg bg-white/[0.03] border border-white/5">
                    <span className="text-sm text-white/60">Final Outcome:</span>
                    <Badge className={creatorWinsAsserted 
                        ? 'bg-[#00FF66]/20 text-[#00FF66]' 
                        : 'bg-[#6F3FCA]/20 text-[#9B6FE8]'
                    }>
                        {creatorWinsAsserted ? 'Creator Wins' : 'Taker Wins'}
                    </Badge>
                </div>
                
                {/* Transaction Info */}
                {settlementHash && (
                    <div className="p-3 rounded-lg bg-[#00FF66]/10 border border-[#00FF66]/20">
                        <div className="text-xs text-[#00FF66] mb-1">Settlement Transaction</div>
                        <a 
                            href={getExplorerUrl(chainId, 'tx', settlementHash)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-mono text-xs text-white hover:text-[#00FF66] flex items-center gap-1"
                        >
                            {settlementHash.slice(0, 10)}...{settlementHash.slice(-8)}
                            <ExternalLink className="w-3 h-3" />
                        </a>
                    </div>
                )}
                
                {/* Error Display */}
                {settleError && (
                    <Alert className="border-red-500/30 bg-red-500/10">
                        <AlertDescription className="text-red-400 text-sm">
                            {settleError.shortMessage || 'Settlement failed. Please try again.'}
                        </AlertDescription>
                    </Alert>
                )}
                
                {/* Settlement Button */}
                <Button
                    className="w-full bg-[#00FF66] text-[#0A0A0C] hover:bg-[#00CC52] font-semibold"
                    onClick={handleSettle}
                    disabled={isSettlePending || isSettleConfirming || isSettleSuccess}
                    data-testid="settle-assertion-btn"
                >
                    {isSettlePending ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Confirm in Wallet...</>
                    ) : isSettleConfirming ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Settling...</>
                    ) : isSettleSuccess ? (
                        <><CheckCircle className="w-4 h-4 mr-2" />Settled!</>
                    ) : (
                        <>
                            <Sparkles className="w-4 h-4 mr-2" />
                            Settle & Claim Winnings
                            <ArrowRight className="w-4 h-4 ml-2" />
                        </>
                    )}
                </Button>
                
                <p className="text-xs text-white/40 text-center">
                    Anyone can call this function. The winner receives their payout automatically.
                </p>
            </CardContent>
        </Card>
    );
}
